import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import ActivePromotionsBanner from '../../shared/ActivePromotionsBanner';

function matchesSelectedRoute(promo, route) {
  return (
    promo.applicable_to === 'all' ||
    promo.applicable_to === 'specific_goods' ||
    (promo.applicable_to === 'cn_gh' && route === 'CN_GH') ||
    (promo.applicable_to === 'tr_gh' && route === 'TR_GH')
  );
}

export default function AdminUpload() {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ transport_mode: 'sea', route: 'CN_GH' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    axios.get('/api/promotions').then((response) => setPromos(response.data)).catch(() => {});
  }, []);

  const routePromos = useMemo(
    () => promos.filter((promo) => matchesSelectedRoute(promo, form.route)),
    [form.route, promos]
  );

  const onDrop = useCallback((files) => {
    if (files[0]) setFile(files[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
  });

  const upload = async () => {
    if (!file) {
      toast.error('Select an Excel file first');
      return;
    }

    setLoading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('transport_mode', form.transport_mode);
    formData.append('route', form.route);

    try {
      const response = await axios.post('/api/admin/shipments/upload', formData);
      setResult(response.data);
      setFile(null);

      if (response.data.appliedPromotions?.length) {
        toast.success(
          `Uploaded. ${response.data.totalItems} items processed and ${response.data.appliedPromotions.length} promotion(s) applied.`
        );
      } else {
        toast.success(`Uploaded. ${response.data.totalItems} items processed.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <Link to="/admin" className="back-btn" aria-label="Back to dashboard">
          &lt;
        </Link>
        <h1>Upload Sheet</h1>
      </div>

      <div className="page-content">
        <div className="card">
          <p style={{ fontWeight: 700, marginBottom: 12 }}>Upload Settings</p>

          <div className="input-group">
            <label>Route</label>
            <select
              className="input-field"
              value={form.route}
              onChange={(event) => setForm({ ...form, route: event.target.value })}
            >
              <option value="CN_GH">China to Ghana</option>
              <option value="TR_GH">Turkey to Ghana</option>
            </select>
          </div>

          <div className="input-group" style={{ marginBottom: 0 }}>
            <label>Transport Mode</label>
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              {[
                { key: 'sea', label: 'Sea Freight', desc: 'Billed by volume: CBM x CBM rate' },
                { key: 'air', label: 'Air Freight', desc: 'Billed by weight: KG x KG rate' },
              ].map((mode) => (
                <div
                  key={mode.key}
                  onClick={() => setForm({ ...form, transport_mode: mode.key })}
                  style={{
                    flex: 1,
                    cursor: 'pointer',
                    borderRadius: 10,
                    padding: '14px 12px',
                    border: `2px solid ${form.transport_mode === mode.key ? 'var(--gold)' : 'var(--border)'}`,
                    background: form.transport_mode === mode.key ? 'var(--surface-alt)' : 'var(--surface)',
                    transition: 'all 0.15s',
                  }}
                >
                  <p style={{ fontWeight: 700, fontSize: 13 }}>{mode.label}</p>
                  <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{mode.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {routePromos.length > 0 && (
          <ActivePromotionsBanner promotions={routePromos} audience="upload" className="upload-promo-banner" />
        )}

        <div className="card" style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)' }}>
          <p style={{ fontSize: 13, color: 'var(--text)', fontWeight: 700, marginBottom: 4 }}>
            {form.transport_mode === 'sea' ? 'Sea freight formula' : 'Air freight formula'}
          </p>
          <p style={{ fontSize: 12, color: 'var(--muted)' }}>
            {form.transport_mode === 'sea'
              ? 'Fee (USD) = Volume (CBM) x CBM rate. Fee (GHS) = Fee (USD) x exchange rate.'
              : 'Fee (USD) = Weight (KG) x KG rate. Fee (GHS) = Fee (USD) x exchange rate.'}
          </p>
          <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
            Rates and exchange rate are managed under Admin Pricing.
          </p>
        </div>

        <p className="section-title">Excel File</p>
        <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
          <input {...getInputProps()} />
          {file ? (
            <div>
              <p style={{ fontWeight: 600, color: '#16a34a' }}>{file.name}</p>
              <p style={{ fontSize: 12, color: 'var(--muted)' }}>Tap to change file</p>
            </div>
          ) : (
            <div>
              <p style={{ fontWeight: 600 }}>Drag and drop Excel file here</p>
              <p>or tap to browse .xlsx and .xls files</p>
            </div>
          )}
        </div>

        <div className="card" style={{ background: 'var(--surface-alt)', marginTop: 8 }}>
          <p style={{ fontSize: 12, color: 'var(--text)', fontWeight: 700, marginBottom: 4 }}>
            Expected Excel columns
          </p>
          <p style={{ fontSize: 12, color: 'var(--muted)' }}>
            Arrival date, shipping mark, tracking number, goods name, quantity, CBM, KG, and remark.
          </p>
        </div>

        <button className="btn btn-primary btn-full" style={{ marginTop: 12 }} onClick={upload} disabled={loading || !file}>
          {loading ? (
            <>
              <span className="spinner" /> Processing...
            </>
          ) : (
            'Upload and Process'
          )}
        </button>

        {result && (
          <div className="card" style={{ background: '#dcfce7', border: '1px solid #86efac', marginTop: 12 }}>
            <p style={{ fontWeight: 700, color: '#15803d', marginBottom: 4 }}>Upload Successful</p>
            <p style={{ fontSize: 13, color: '#166534' }}>Batch ID: #{result.uploadId}</p>
            <p style={{ fontSize: 13, color: '#166534' }}>Items processed: {result.totalItems}</p>
            <p style={{ fontSize: 13, color: '#166534' }}>
              Transport: {result.transportMode === 'sea' ? 'Sea Freight' : 'Air Freight'}
            </p>
            <p style={{ fontSize: 13, color: '#166534' }}>
              Customers were notified in-app and through configured email and SMS channels.
            </p>

            {result.appliedPromotions?.length > 0 ? (
              <div style={{ marginTop: 10 }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 6 }}>
                  Promotions applied in this batch
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {result.appliedPromotions.map((promo) => (
                    <span key={promo.id} className="badge badge-gold">
                      {promo.title} - {promo.discount_percent}% off
                    </span>
                  ))}
                </div>
              </div>
            ) : result.activePromotions?.length > 0 ? (
              <p style={{ fontSize: 12, color: '#166534', marginTop: 10 }}>
                Active promotions exist, but none of the imported goods matched them in this batch.
              </p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
